import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Package } from '@phosphor-icons/react';
import HaleAdminLayout from '../../components/hale/HaleAdminLayout';
import { T, FONT, PageHeading, ConsoleCard, StatusPill, EmptyState, Spinner } from '../hale-console/_theme';

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'under_review', label: 'Under review' },
  { key: 'information_requested', label: 'Info requested' },
  { key: 'approved', label: 'Approved' },
  { key: 'conditional_approval', label: 'Conditional' },
  { key: 'not_approved', label: 'Not approved' },
];

export default function HaleReviewedQueue() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/api/hale-reviewed/admin/queue${f ? `?status=${f}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/api/hale-reviewed/admin/stats`, { withCredentials: true }),
      ]);
      setItems(q.data); setStats(s.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  return (
    <HaleAdminLayout>
      <PageHeading title="Hale Reviewed Queue" sub="Products submitted by Hale brands, awaiting and in review against the Hale Reviewed standard." testid="hale-queue-title" />

      {/* stat chips */}
      {stats && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="hale-queue-stats">
          {FILTERS.filter((f) => f.key).map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className="px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors"
              style={{ borderColor: filter === f.key ? T.RED : T.LINE, color: filter === f.key ? T.RED : T.SOFT, backgroundColor: T.WHITE }}>
              {f.label} · {stats[f.key] ?? 0}
            </button>
          ))}
          <button onClick={() => setFilter('')} className="px-3 py-1.5 rounded-full text-[11px] font-semibold border" style={{ borderColor: filter === '' ? T.RED : T.LINE, color: filter === '' ? T.RED : T.SOFT, backgroundColor: T.WHITE }}>
            All · {stats.total ?? 0}
          </button>
        </div>
      )}

      {loading && <Spinner label="Loading queue…" />}

      {!loading && items.length === 0 && (
        <EmptyState icon={Package} title="No products in this view" desc="Submitted products from Hale brands will appear here for review." testid="hale-queue-empty" />
      )}

      {!loading && items.length > 0 && (
        <ConsoleCard className="!p-0 overflow-hidden" data-testid="hale-queue-list">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b text-[10px] uppercase tracking-[0.14em]" style={{ borderColor: T.LINE, color: T.MUTED, fontFamily: FONT.MONO }}>
            <div className="col-span-4">Product</div><div className="col-span-3">Brand</div><div className="col-span-2">Category</div><div className="col-span-2">Submitted</div><div className="col-span-1" />
          </div>
          {items.map((p) => (
            <Link key={p.id} to={`/admin/hale-reviewed/${p.id}`} data-testid={`hale-queue-row-${p.id}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b last:border-0 items-center hover:bg-[#FAFAFA] transition-colors" style={{ borderColor: T.LINE }}>
              <div className="md:col-span-4">
                <p className="text-[14px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{p.product_name}</p>
                <div className="mt-1 md:hidden"><StatusPill status={p.status} /></div>
              </div>
              <div className="md:col-span-3 text-[13px]" style={{ color: T.SOFT }}>{p.brand_name}</div>
              <div className="md:col-span-2 text-[13px]" style={{ color: T.MUTED }}>{p.category}</div>
              <div className="md:col-span-2 text-[13px]" style={{ color: T.MUTED }}>{p.submitted_at ? new Date(p.submitted_at).toLocaleDateString() : '—'}</div>
              <div className="md:col-span-1 flex items-center justify-between md:justify-end gap-2">
                <span className="hidden md:inline"><StatusPill status={p.status} /></span>
                <ArrowRight size={15} weight="bold" style={{ color: T.MUTED }} />
              </div>
            </Link>
          ))}
        </ConsoleCard>
      )}
    </HaleAdminLayout>
  );
}
